import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Phone, Mail } from "lucide-react";
import { CONTACT } from "@/data/products";
import { useProducts } from "@/context/ProductsContext";
import { imgSrc } from "@/lib/api";
import { MaskLines, FadeUp, Eyebrow } from "@/components/site/Reveal";
import { ProductCard } from "@/components/site/ProductCard";

const SpecList = ({ title, items, testid }) => (items?.length ? (
  <div data-testid={testid}>
    <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-700">{title}</p>
    <ul className="mt-4 border-t border-black/15">
      {items.map((t) => (
        <li key={t} className="flex items-center gap-3 border-b border-black/15 py-3.5 text-base"><span className="h-1.5 w-1.5 bg-[#FF5A00]" />{t}</li>
      ))}
    </ul>
  </div>
) : null);

export default function ProductDetail() {
  const { slug } = useParams();
  const { products: PRODUCTS, loaded } = useProducts();
  const p = PRODUCTS.find((x) => x.slug === slug);
  if (!p) return loaded ? <Navigate to="/products" replace /> : <div className="min-h-screen" />;
  const related = PRODUCTS.filter((x) => x.slug !== p.slug && x.category === p.category).slice(0, 3);
  const mail = `mailto:${CONTACT.email}?subject=${encodeURIComponent(`Enquiry: ${p.name}`)}`;

  return (
    <div data-testid="product-detail-page" key={p.slug}>
      <section className="mx-auto grid max-w-[1440px] gap-12 px-5 pb-20 pt-[120px] md:grid-cols-12 md:px-10 md:pt-[150px]">
        <div className="md:col-span-6">
          <Link to="/products" data-testid="back-to-catalogue" className="group mb-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-700 hover:text-[#0B0B0B]">
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" /> Catalogue
          </Link>
          <motion.div initial={{ clipPath: "inset(0 0 100% 0)" }} animate={{ clipPath: "inset(0 0 0% 0)" }} transition={{ duration: 1.2, ease: [0.76, 0, 0.24, 1] }}
>
            <div className="spec-frame relative aspect-square overflow-hidden [clip-path:polygon(0_0,100%_0,100%_88%,88%_100%,0_100%)]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(255,196,0,0.4),transparent_62%)]" />
            <img data-testid="product-image" src={imgSrc(p.image)} alt={p.name} className="absolute inset-[10%] h-[80%] w-[80%] object-contain" />
            <span className="absolute bottom-5 left-5 h-4 w-4 rounded-full" style={{ background: p.swatch }} />
            </div>
          </motion.div>
        </div>
        <div className="md:col-span-6 md:pl-6 md:pt-14">
          <Eyebrow>{p.category}{p.isNew ? " · New" : ""}</Eyebrow>
          <h1 data-testid="product-name" className="font-wide mt-6 text-4xl font-black uppercase leading-[0.92] tracking-tight sm:text-5xl lg:text-6xl">
            <MaskLines lines={[p.name]} delay={0.2} />
          </h1>
          <FadeUp delay={0.3}>
            <p className="mt-6 text-lg font-medium text-[#0B0B0B]">{p.tagline}</p>
            <p data-testid="product-description" className="mt-4 text-base leading-relaxed text-zinc-700">{p.description}</p>
            <div className="mt-8 inline-flex items-center gap-3 bg-[#FFC400] px-4 py-2.5 font-mono text-xs uppercase tracking-[0.15em]" data-testid="product-moq">
              Minimum order · 100 kg
            </div>
            <div className="mt-10 grid gap-10 sm:grid-cols-2">
              <SpecList title="Applications" items={p.applications} testid="product-applications" />
              <SpecList title="Key features" items={p.features} testid="product-features" />
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              <a href={`tel:${CONTACT.tel}`} data-testid="detail-call-btn" className="flex items-center gap-3 bg-[#0B0B0B] px-6 py-4 font-mono text-xs uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-[#FF5A00]">
                <Phone className="h-4 w-4" /> Call {CONTACT.phone}
              </a>
              <a href={mail} data-testid="detail-email-btn" className="flex items-center gap-3 border border-[#0B0B0B] px-6 py-4 font-mono text-xs uppercase tracking-[0.18em] transition-colors duration-300 hover:bg-[#FFC400]">
                <Mail className="h-4 w-4" /> Email enquiry
              </a>
            </div>
          </FadeUp>
        </div>
      </section>
      {related.length > 0 && (
        <section className="border-t border-black/10 bg-[#FAFAF7]">
          <div className="mx-auto max-w-[1440px] px-5 py-20 md:px-10">
            <Eyebrow>More in {p.category}</Eyebrow>
            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r, i) => <ProductCard key={r.slug} product={r} index={i} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
